/** @jsxRuntime classic */
/** @jsx jsx */
'use client';
import { css, jsx } from '@emotion/react';
import styled from '@emotion/styled';
import React, { FunctionComponent, Fragment } from 'react';
import { colors, typography as commonTypography, size } from '@argo/principles';
import { Scroller, ChevronRightIcon } from '@argo/ui-elements';
import { rgba } from '@argo/utils';


export interface BreadcrumbItemProps {
  name?: string;
  url?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export interface BreadcrumbsProps {
  autoHideScroller?: boolean;
  breadcrumbItems: Array<BreadcrumbItemProps>;
  breadcrumbMarginTop?: number;
  breadcrumbMarginBottom?: number;
  showScrollerFader?: boolean;
  scrollerFaderColor?: string;
  hidden?: boolean;
  defaultScrollDirection?: 'left' | 'right';
  isDefaultScrollDirection?: boolean;
  analytics?: any;
  breadcrumbItemColor?: string;
  breadcrumbIconColor?: string;
  breadcrumbCurrentColor?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const breadcrumbStyles = ({
  theme: {
    breadcrumbs: {
      padding = '',
      typography = '',
    } = {},
  } = {},
}) => css`
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    padding: ${padding};
    text-align: left;
    ${typography};
    &:first-of-type {
      padding-left: 0;
    }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  padding: 0;
`;

const BreadcrumbCurrentPage = styled.div<{ breadcrumbCurrentColor: string }>`
  ${breadcrumbStyles}
  font-weight: ${commonTypography.weight.semiBold};
  &:first-of-type {
    padding-left: ${size.xs}px;
  }
  color: ${({ breadcrumbCurrentColor }): string => breadcrumbCurrentColor};
`;

const BreadcrumbItem = styled.a<{ breadcrumbItemColor?: string }>`
  ${breadcrumbStyles}
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: none;
    /* This is a UX-approved exception to allow only breadcrumbs to be underlined when focused */
    text-decoration: underline;
  }
  &:active {
    background-color: ${rgba(colors.neutral.trueGray, 0.4)};
    border-radius: ${size.micro}px;
    box-shadow: none;
  }
  &:first-of-type {
    span {
      padding-left: ${size.none};
    }
  }
  ${({ breadcrumbItemColor }): string =>
    breadcrumbItemColor
      ? `color: ${breadcrumbItemColor};`
      : ''}
`;

const BreadcrumbIcon = styled.span`
  ${breadcrumbStyles}
`;

//  hack to silent ResizeObserver error caused by simplebar
//  https://github.com/Grsmto/simplebar/pull/288
const BreadcrumbWrapper = styled.div`
  min-height: 21px;
  ${({ hidden }) => 
    hidden
      ? `
    visibility: hidden;
    
    * {
      visibility: hidden;
    }
    `
      : `
    visibility: visible;
    * {
      visibility: visible;
    }
  `}
`;

const Breadcrumb: FunctionComponent<BreadcrumbsProps> = ({
  autoHideScroller = false,
  breadcrumbItems = [],
  breadcrumbMarginTop,
  breadcrumbMarginBottom,
  showScrollerFader = true,
  scrollerFaderColor = colors.primary.white,
  hidden,
  defaultScrollDirection = 'left',
  isDefaultScrollDirection = false,
  analytics,
  breadcrumbItemColor,
  breadcrumbIconColor = colors.neutral.charcoal,
  breadcrumbCurrentColor = colors.neutral.black,
  ...rest
}) => {
  const wrapperCss = css`
    margin-bottom: ${(breadcrumbMarginBottom || breadcrumbMarginBottom === 0 ? breadcrumbMarginBottom : size.none) - size.sm}px;
    margin-top: ${(breadcrumbMarginTop || breadcrumbMarginTop === 0 ? breadcrumbMarginTop : size.none) - size.sm}px;
  `;

  return (
    <BreadcrumbWrapper hidden={hidden} css={wrapperCss}>
      <Scroller
        autoHideScroller={autoHideScroller}
        horizontal
        small
        fader={showScrollerFader}
        faderColor={scrollerFaderColor}
        defaultScrollDirection={defaultScrollDirection}
        isDefaultScrollDirection={isDefaultScrollDirection}
      >
        <BreadcrumbContainer data-automation="breadcrumb-container" {...rest}>
          {breadcrumbItems.map((item, index) => {
            const analyticsAttr = analytics ? {
              'data-testid': `breadcrumb_${index + 1}`,
              onClick: (event) => {
                const breadcrumbName = typeof event?.target?.innerText === 'string' ? event.target.innerText.toLowerCase().replace(/\s{1,}/g, '-') : '';
            
                if (breadcrumbName) {
                  analytics.push(dataLayer => ({
                    type: 'click',
                    googleAnalytics: {
                      props: {
                        event: 'analytics_event',
                        eventElement: 'breadcrumbs',
                        eventAction: 'click',
                        eventResult: `breadcrumb-${index + 1}::${breadcrumbName}`,
                        eventNonInteractive: false,
                      },
                    },
                  }));
                }
              },
            } : {};
          
            if (index === breadcrumbItems.length - 1) {
              return (
                <BreadcrumbCurrentPage
                  data-cy={'BreadcrumbCurrentPage'}
                  key={index}
                  breadcrumbCurrentColor={breadcrumbCurrentColor}
                >
                  {item.name}
                </BreadcrumbCurrentPage>
              );
            } else {
              return (
                <Fragment key={index}>
                  <BreadcrumbItem
                    {...analyticsAttr}
                    data-cy={`breadcrumbItem-${index + 1}`}
                    href={item.url}
                    breadcrumbItemColor={breadcrumbItemColor}
                  >
                    <span css={{ padding: `${size.sm}px ${size.xs}px` }}>
                      {item.name}
                    </span>
                  </BreadcrumbItem>
                  <BreadcrumbIcon>
                    <ChevronRightIcon
                      color={breadcrumbIconColor}
                      size="9"
                    />
                  </BreadcrumbIcon>
                </Fragment>
              );
            }
          })}
        </BreadcrumbContainer>
      </Scroller>
    </BreadcrumbWrapper>
  );
};


Breadcrumb.displayName = 'Breadcrumb';

export default Breadcrumb;
